import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { useSessionStatus } from "@/features/chat/api/sessions";
import { SessionItem } from "@/features/chat/components/SessionItem";
import { useStreamingSessionIds } from "@/features/chat/streams/streamSelectors";
import { CurationStatusIcon } from "@/features/data-curation/components/CurationStatusIcon";
import {
  useDeleteExtractionDataset,
  useExtractionDatasets,
  usePrefetchExtractionDataset,
} from "@/features/data-curation/hooks/useCuration";
import {
  useDeletePendingCurationChat,
  usePendingCurationChats,
} from "@/features/data-curation/hooks/usePendingCurationChats";
import {
  curationListStatus,
  curationListStatusLabel,
} from "@/features/data-curation/status";
import type { ExtractionDataset } from "@/features/data-curation/types";
import { DATASETS_ROUTE } from "@/features/datasets/routes";
import { leftSidebarOpenAtom } from "@/utils/sidebarStore";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

type CurationEntry = {
  key: string;
  /** "pending" is a new curation whose agent has not created it yet. */
  kind: "dataset" | "pending";
  /** The dataset ID, or the chat session ID of a pending curation. */
  id: string;
  title: string;
  chatSessionId: string | null;
  dataset: ExtractionDataset | null;
};

type CurationListItemProps = {
  entry: CurationEntry;
  isActive: boolean;
  streaming: boolean;
  revealedKey: string | null;
  onReveal: (key: string | null) => void;
  onNavigate: (key: string) => void;
  onDelete: (key: string) => void;
};

/** A pointer that rests this long on a dataset loads it, so that a click opens it at once. */
const PREFETCH_DELAY_MS = 150;

const CurationListItem = ({
  entry,
  streaming,
  revealedKey,
  ...rest
}: CurationListItemProps) => {
  // A pending curation has no dataset to report its agent, so ask its chat.
  const chatStatus = useSessionStatus(
    entry.kind === "pending" ? (entry.chatSessionId ?? undefined) : undefined,
    !streaming,
  );
  const status = curationListStatus(
    entry.dataset,
    streaming ||
      Boolean(entry.dataset?.agent_running) ||
      chatStatus.data?.status === "running",
  );
  const prefetchDataset = usePrefetchExtractionDataset();
  const prefetchTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(prefetchTimer.current), []);
  const prefetch = () => {
    if (entry.dataset) void prefetchDataset(entry.dataset);
  };
  return (
    <div
      onPointerEnter={() => {
        window.clearTimeout(prefetchTimer.current);
        prefetchTimer.current = window.setTimeout(prefetch, PREFETCH_DELAY_MS);
      }}
      onPointerLeave={() => window.clearTimeout(prefetchTimer.current)}
      onPointerDown={prefetch}
      onFocus={prefetch}
    >
      <SessionItem
        sessionId={entry.key}
        title={entry.title}
        isStreaming={streaming}
        status={{
          icon: <CurationStatusIcon status={status} />,
          label: curationListStatusLabel[status],
        }}
        noun="dataset"
        revealedSessionId={revealedKey}
        {...rest}
      />
    </div>
  );
};

type CurationSessionListProps = {
  /** Called after the user navigates (used to close the mobile sidebar). */
  onNavigate?: () => void;
  /** The page the datasets open on: /datasets, or /datasets/legacy. */
  routeBase?: string;
  /** The read-only old datasets: no new datasets that are still setting up. */
  archived?: boolean;
};

/**
 * The curations list for the WorkspaceSidebar, built like ChatSessionList:
 * new curations that are still setting up first, then the extraction datasets.
 */
export const CurationSessionList = ({
  onNavigate,
  routeBase = DATASETS_ROUTE,
  archived = false,
}: CurationSessionListProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeDatasetId = searchParams.get("dataset");
  const activeChatId = searchParams.get("chat");
  const { pending: pendingChats } = usePendingCurationChats();
  const pending = useMemo(() => (archived ? [] : pendingChats), [archived, pendingChats]);
  const {
    data: datasets,
    isLoading,
    error,
  } = useExtractionDatasets({ refetchInterval: pending.length ? 2_000 : 5_000 });
  const deleteDataset = useDeleteExtractionDataset();
  const { deletePendingChat } = useDeletePendingCurationChat();
  const streamingSessions = useStreamingSessionIds();
  // The target outlives `deleteOpen` so the dialog keeps its text while it closes.
  const [deleteTarget, setDeleteTarget] = useState<CurationEntry | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  // Reset revealed swipe state when the mobile sidebar closes
  const sidebarOpen = useAtomValue(leftSidebarOpenAtom);
  const prevSidebarOpen = useRef(sidebarOpen);
  useEffect(() => {
    if (prevSidebarOpen.current && !sidebarOpen) {
      setRevealedKey(null);
    }
    prevSidebarOpen.current = sidebarOpen;
  }, [sidebarOpen]);

  const entries = useMemo<CurationEntry[]>(() => {
    const claimedChats = new Set(
      (datasets ?? []).map((item) => item.chat_session_id),
    );
    return [
      ...pending
        .filter((chat) => !claimedChats.has(chat.sessionId))
        .map((chat) => ({
          key: `chat:${chat.sessionId}`,
          kind: "pending" as const,
          id: chat.sessionId,
          title: "New dataset",
          chatSessionId: chat.sessionId,
          dataset: null,
        })),
      ...(datasets ?? []).map((item) => ({
        key: `dataset:${item.id}`,
        kind: "dataset" as const,
        id: item.id,
        title: item.name,
        chatSessionId: item.chat_session_id ?? null,
        dataset: item,
      })),
    ];
  }, [datasets, pending]);

  const entryByKey = useMemo(
    () => new Map(entries.map((entry) => [entry.key, entry])),
    [entries],
  );

  const isActive = (entry: CurationEntry) =>
    entry.kind === "dataset"
      ? entry.id === activeDatasetId
      : entry.id === activeChatId;

  const handleReveal = useCallback((key: string | null) => {
    setRevealedKey(key);
  }, []);

  const handleNavigate = useCallback(
    (key: string) => {
      const entry = entryByKey.get(key);
      if (!entry) return;
      setRevealedKey(null);
      const param = entry.kind === "dataset" ? "dataset" : "chat";
      navigate(`${routeBase}?${new URLSearchParams({ [param]: entry.id })}`);
      onNavigate?.();
    },
    [entryByKey, navigate, onNavigate, routeBase],
  );

  const handleDelete = useCallback(
    (key: string) => {
      const entry = entryByKey.get(key);
      setRevealedKey(null);
      if (!entry) return;
      setDeleteTarget(entry);
      setDeleteOpen(true);
    },
    [entryByKey],
  );

  const confirmDelete = () => {
    setDeleteOpen(false);
    if (!deleteTarget) return;
    const target = deleteTarget;
    const leaveIfOpen = () => {
      if (isActive(target)) navigate(routeBase, { replace: true });
    };
    if (target.kind === "pending") {
      void deletePendingChat(target.id, leaveIfOpen);
    } else {
      deleteDataset.mutate(target.id, {
        onSuccess: leaveIfOpen,
        onError: (deleteError) =>
          toast.error(
            deleteError instanceof Error
              ? deleteError.message
              : "Could not delete this dataset.",
          ),
      });
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto min-h-0 px-3">
        {isLoading && entries.length === 0 && (
          <div className="space-y-1 px-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-8 animate-pulse rounded-[3px] bg-zinc-100"
              />
            ))}
          </div>
        )}

        {error && !datasets && (
          <p role="alert" className="px-2 py-4 text-center text-xs text-red-700">
            Could not load datasets.
          </p>
        )}

        {!isLoading && !error && entries.length === 0 && (
          <p className="px-2 py-4 text-center text-xs text-zinc-500">
            No datasets yet
          </p>
        )}

        <div className="space-y-px">
          {entries.map((entry) => (
            <CurationListItem
              key={entry.key}
              entry={entry}
              isActive={isActive(entry)}
              streaming={
                entry.chatSessionId ? streamingSessions.has(entry.chatSessionId) : false
              }
              revealedKey={revealedKey}
              onReveal={handleReveal}
              onNavigate={handleNavigate}
              onDelete={handleDelete}
            />
          ))}
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deleteTarget?.kind === "pending"
                ? "Delete new dataset?"
                : "Delete dataset?"}
            </DialogTitle>
            <DialogDescription>
              {deleteTarget?.kind === "pending"
                ? "This stops its agent and deletes its chat. It has no dataset yet, so nothing else is removed."
                : `This permanently deletes “${deleteTarget?.title ?? ""}” and its evidence repository. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
